import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, BarChart2, TrendingUp, Lightbulb, ClipboardList, Users, LineChart } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { DjayNarrator, DjayBubble } from '../../../mascot';
import type { AccountRole } from '../../types';
import { OnboardingProgress } from './OnboardingProgress';
import { getRoleThemeClasses } from '../../logic/uiHelpers';
import { dataLayer } from '../../../data/dataLayer';
interface OnboardingWelcomeStepProps {
  role: AccountRole;
  name: string;
  groupLevel?: number;
  totalSteps?: number;
  onNext: () => void;
  onGroupChange?: (group: number) => void;
}
export function OnboardingWelcomeStep({
  role,
  name,
  groupLevel = 6,
  totalSteps = 2,
  onNext,
  onGroupChange
}: OnboardingWelcomeStepProps) {
  const theme = getRoleThemeClasses(role);
  const getRoleConfig = () => {
    switch (role) {
      case 'learner':
        return {
          emotion: 'excited' as const,
          message: `Welkom ${name}! Ik ben Djay, jouw leermaatje. Ik geef je graag een rondleiding door de app.`
        };
      case 'parent':
        return {
          emotion: 'happy' as const,
          message: 'Welkom! Hier kunt u de voortgang van uw kind volgen.'
        };
      case 'teacher':
        return {
          emotion: 'celebrating' as const,
          message: 'Welkom! Laten we uw klas klaarzetten.'
        };
      default:
        return {
          emotion: 'happy' as const,
          message: 'Welkom!'
        };
    }
  };
  const config = getRoleConfig();
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className="w-full max-w-md mx-auto" data-id="element-114">
      {/* Mascot Section */}
      <div className="flex flex-col items-center mb-6 relative" data-id="element-115">
        <motion.div animate={{
        y: [0, -8, 0]
      }} transition={{
        duration: 3,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-116">
          <DjayNarrator emotion={config.emotion} size="lg" data-id="element-117" />
        </motion.div>
        <div className="mt-3 w-full max-w-[260px]" data-id="element-118">
          <DjayBubble message={config.message} position="top" colorScheme={theme.colorScheme} autoDismissMs={0} data-id="element-119" />
        </div>
      </div>

      {/* Card */}
      <div className={`bg-white rounded-3xl p-6 sm:p-8 shadow-lg border ${theme.border} relative overflow-hidden`} data-id="element-120">
        <div className={`absolute top-0 left-0 w-full h-2 bg-gradient-to-r ${theme.gradient}`} data-id="element-121" />

        <OnboardingProgress currentStep={0} totalSteps={totalSteps} role={role} data-id="element-122" />

        {role === 'learner' && <div className="space-y-6" data-id="element-123">
            <div className="text-center" data-id="element-124">
              <h2 className="text-2xl font-black text-gray-900 mb-2" data-id="element-125">
                Klaar voor de start? 🚀
              </h2>
              <p className="text-gray-600 font-medium" data-id="element-126">
                Je gaat oefenen op het niveau van{' '}
                <span className="font-bold text-orange-600" data-id="element-127">
                  groep {groupLevel}
                </span>
                .
              </p>
            </div>

            <div className="bg-orange-50 border border-orange-100 rounded-2xl p-5 space-y-4" data-id="element-128">
              <motion.div className="flex items-center gap-3" initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.1
          }} data-id="element-129">
                <div className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center text-orange-500 flex-shrink-0" data-id="element-130">
                  <span className="text-xl" data-id="element-131">🗺️</span>
                </div>
                <p className="text-sm font-medium text-orange-900" data-id="element-132">
                  Ontdek waar je alles kunt vinden in de app
                </p>
              </motion.div>
              <motion.div className="flex items-center gap-3" initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.2
          }} data-id="element-133">
                <div className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center text-orange-500 flex-shrink-0" data-id="element-134">
                  <span className="text-xl" data-id="element-135">⭐</span>
                </div>
                <p className="text-sm font-medium text-orange-900" data-id="element-136">
                  Zie hoe je XP en nieuwe looks verdient
                </p>
              </motion.div>
            </div>
          </div>}

        {role === 'parent' && <div className="space-y-6" data-id="element-137">
            <h2 className="text-xl font-bold text-center text-gray-900" data-id="element-138">
              Wat kunt u hier doen?
            </h2>
            <ul className="space-y-4" data-id="element-139">
              <li className="flex items-center gap-3 text-gray-700" data-id="element-140">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-141">
                  <BarChart2 className="w-5 h-5 text-emerald-600" data-id="element-142" />
                </div>
                <span data-id="element-143">Voortgang per vak volgen</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700" data-id="element-144">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-145">
                  <TrendingUp className="w-5 h-5 text-emerald-600" data-id="element-146" />
                </div>
                <span data-id="element-147">Groei over tijd bekijken</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700" data-id="element-148">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-149">
                  <Lightbulb className="w-5 h-5 text-emerald-600" data-id="element-150" />
                </div>
                <span data-id="element-151">Persoonlijke oefentips</span>
              </li>
            </ul>
          </div>}

        {role === 'teacher' && <div className="space-y-6" data-id="element-152">
            <h2 className="text-xl font-bold text-center text-gray-900" data-id="element-153">
              Wat kunt u hier doen?
            </h2>
            <ul className="space-y-4" data-id="element-154">
              <li className="flex items-center gap-3 text-gray-700" data-id="element-155">
                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-156">
                  <ClipboardList className="w-5 h-5 text-indigo-600" data-id="element-157" />
                </div>
                <span data-id="element-158">Dagelijks klas-overzicht</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700" data-id="element-159">
                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-160">
                  <Users className="w-5 h-5 text-indigo-600" data-id="element-161" />
                </div>
                <span data-id="element-162">Leerlingen beheren</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700" data-id="element-163">
                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-164">
                  <LineChart className="w-5 h-5 text-indigo-600" data-id="element-165" />
                </div>
                <span data-id="element-166">Groei en trends volgen</span>
              </li>
            </ul>
          </div>}

        <div className="mt-8 flex flex-col gap-3" data-id="element-167">
          {role === 'learner' ? <>
              <Button onClick={async () => {
            try {
              const prefs = await dataLayer.getLearnerPreferences();
              await dataLayer.saveLearnerPreferences({
                ...prefs,
                pendingAppTour: true,
                tourCompleted: false
              });
            } catch (e) {}
            onNext();
          }} className={`w-full py-6 text-lg text-white ${theme.buttonBg}`} data-id="element-168">
                Start de rondleiding
                <ArrowRight className="w-5 h-5 ml-2" data-id="element-169" />
              </Button>
              <Button variant="ghost" onClick={async () => {
            try {
              const prefs = await dataLayer.getLearnerPreferences();
              await dataLayer.saveLearnerPreferences({
                ...prefs,
                pendingAppTour: false,
                tourCompleted: true
              });
            } catch (e) {}
            onNext();
          }} className="w-full text-slate-500 hover:text-slate-700" data-id="element-170">
                Sla over
              </Button>
            </> : <Button onClick={onNext} className={`w-full py-6 text-lg text-white ${theme.buttonBg}`} data-id="element-171">
              Verder
              <ArrowRight className="w-5 h-5 ml-2" data-id="element-172" />
            </Button>}
        </div>
      </div>
    </motion.div>;
}